import React, { memo, useContext, useEffect } from 'react';
import { Handle, Position, useUpdateNodeInternals, type NodeProps } from 'reactflow';
import { Box, Typography } from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import BoltIcon from '@mui/icons-material/Bolt';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { RuleFlowNodeType, RuleFlowRecipient } from '../../../services/channelRuleFlowsService';
// Read-only reuse of a plain, stateless context object (and its type) already exported by the
// legacy canvas's node — same pattern WorkflowEdge.tsx already relies on for its own
// straight-vs-bezier decision. Importing a context doesn't run or depend on any of
// PrimitiveNode.tsx's own component logic, so this has zero effect on the legacy canvas.
import { LayoutDirectionContext } from './PrimitiveNode';

/**
 * Business_rule_canvas branch (PRISM7_CHANNEL_RULES_CANVAS_TDD.md §7) — a business-rule-authoring
 * node type, deliberately separate from PrimitiveNode.tsx: that component is keyed by the
 * Temporal execution engine's own primitive enum (START/CALL_API/NOTIFY/...), a different
 * abstraction level from the plain-English TRIGGER/ACTION/CONDITION vocabulary a rules-text
 * parse naturally produces. Reusing PrimitiveNode would mean lossily mapping one vocabulary onto
 * the other; this component keeps them separate while still sharing the same reactflow
 * node-type-registration mechanism and reusing WorkflowEdge.tsx unchanged for connections.
 */
export interface RuleNodeData {
  nodeType: RuleFlowNodeType;
  label: string;
  /** Recipients shown as a short summary line (e.g. an approval ACTION's recipients[]). A null
   * email (source: 'missing') renders as "Unassigned" rather than being silently dropped, so an
   * unresolved recipient stays visible on the card until someone assigns it. */
  recipients?: RuleFlowRecipient[];
  /** Set once from the server's own node data at load/re-parse time (RuleFlowCanvas's
   * docToRfNodes), never from the live/edited `recipients` above — see its own doc there for
   * the bug this exists to prevent (clearing every recipient must not hide the picker needed
   * to pick a replacement). True only for an ACTION whose recipients arrived non-empty. */
  hasRecipientConcept?: boolean;
  /** ADR §3 — set by RuleFlowCanvas's docToRfNodes (ruleFlowValidation.isNodeIncomplete). An
   * approval-requiring ACTION with no resolved recipient hard-blocks as incomplete: a distinct,
   * more severe visual state than the plain "Unassigned" label a fallback action gets. */
  isIncomplete?: boolean;
  /** Fix 14/Item 7 — combined from two sources: the FE-derived missing-recipient check
   * (ruleFlowValidation.nodeRecommendationText) and the backend's node.recommendations[] (parse,
   * correlated from business_rule_validator.py). Purely informational — never blocks anything,
   * unlike isIncomplete's hard-block red state above, which still wins the icon/color slot when
   * both are true; any recommendationTexts still render below regardless. */
  recommendationTexts?: string[];
  /** FIX 10 (frontend handover) — count of edges whose source is this node, computed in
   * RuleFlowCanvas's docToRfNodes from the flow's own edges array. Only meaningful for CONDITION
   * nodes: a value of 2+ gets a distinct branch visual; 1 (today's only real case) or 0 renders
   * exactly as before this fix. */
  branchCount?: number;
  /** FIX 9/11 (frontend handover) — set by RuleFlowCanvas's displayNodes overlay when this node
   * has a staged, unsaved rename pending (never persisted). Purely a display flag — never sent
   * to the backend. */
  isPendingEdit?: boolean;
  /** PRISM7_CHANNEL_RULES_NODE_DESCRIPTIONS_TDD §3.1/§6 — the persisted node annotation, set by
   * RuleFlowCanvas's docToRfNodes straight from the server's node.description. Shown as a
   * subtitle under the label. The inspector's own Description field (RuleFlowCanvas.tsx) edits
   * this via its own PATCH-only save path — there's no staged/unsaved variant of it here, unlike
   * the Label field's isPendingEdit below, since a description edit is never batched. */
  description?: string;
  /** Mirrors the server's node.description_source (TDD §3.1) — drives the "Custom description"
   * indicator. Absent/'generated' shows nothing extra; 'user' means a human override. */
  descriptionSource?: 'generated' | 'user';
  /** FIX 13-FE (delete-node handover doc §3.5) — set by RuleFlowCanvas's displayNodes overlay
   * when this node is staged for deletion. The node stays on the canvas until Save actually
   * removes it (so Discard can restore it), marked with a strikethrough label and reduced
   * opacity — visually distinct from isPendingEdit's dashed border, since this node is *leaving*,
   * not changing. */
  pendingDelete?: boolean;
}

export const RULE_NODE_RF_TYPE = 'ruleNode';

const RULE_NODE_ICONS: Record<RuleFlowNodeType, React.ElementType> = {
  TRIGGER: PlayCircleOutlineIcon,
  ACTION: BoltIcon,
  CONDITION: CallSplitIcon,
};

export const RULE_NODE_COLORS: Record<RuleFlowNodeType, string> = {
  TRIGGER: '#1976d2',
  ACTION: '#2e7d32',
  CONDITION: '#ed6c02',
};

function RuleNode({ id, data, selected }: NodeProps<RuleNodeData>) {
  const Icon = RULE_NODE_ICONS[data.nodeType];
  const hasRecommendation = (data.recommendationTexts?.length ?? 0) > 0;
  const color = data.isIncomplete
    ? '#d32f2f'
    : hasRecommendation
      ? '#0288d1'
      : RULE_NODE_COLORS[data.nodeType];
  const showTargetHandle = data.nodeType !== 'TRIGGER';
  const layoutDirection = useContext(LayoutDirectionContext);
  const isHorizontal = layoutDirection === 'horizontal';
  // FIX 10 — a CONDITION with exactly 1 outgoing edge (or 0, e.g. a dangling draft node) is
  // today's only real case and must render identically to before this fix; only 2+ gets the
  // distinct branch treatment.
  const isBranchingCondition = data.nodeType === 'CONDITION' && (data.branchCount ?? 0) >= 2;

  // React Flow caches each node's handle bounding boxes and does not recompute them just
  // because a handle's position prop changes on re-render — without this, the dots move to the
  // new side but edges keep drawing toward the old cached coordinates (same fix
  // PrimitiveNode.tsx already applies for its own horizontal/vertical toggle).
  const updateNodeInternals = useUpdateNodeInternals();
  useEffect(() => {
    updateNodeInternals(id);
  }, [layoutDirection, id, updateNodeInternals]);

  return (
    <Box
      data-testid={data.pendingDelete ? 'pending-delete-node' : undefined}
      sx={{
        minWidth: 180,
        maxWidth: 260,
        borderRadius: 2,
        border: '2px solid',
        // FIX 9/11 — a dashed border is this card's only "unsaved" cue; selected/incomplete
        // color logic above is untouched so this layers on top rather than replacing it.
        borderStyle: data.isPendingEdit ? 'dashed' : 'solid',
        borderColor: selected ? color : `${color}55`,
        backgroundColor: 'background.paper',
        boxShadow: selected ? 3 : 1,
        overflow: 'hidden',
        position: 'relative',
        // FIX 13-FE — this node is leaving, not changing, so it gets a distinct dimmed treatment
        // rather than isPendingEdit's dashed border.
        opacity: data.pendingDelete ? 0.5 : 1,
      }}
    >
      {data.isPendingEdit && (
        <Box
          data-testid="pending-edit-indicator"
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: '#ed6c02',
          }}
        />
      )}
      {showTargetHandle && (
        <Handle type="target" position={isHorizontal ? Position.Left : Position.Top} />
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          backgroundColor: `${color}15`,
          // FIX 10 — dashed bottom border on a branching CONDITION's header only, a second subtle
          // cue alongside the diamond accent below that this node forks the flow.
          ...(isBranchingCondition && { borderBottom: `1px dashed ${color}` }),
        }}
      >
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18 }}>
          {isBranchingCondition && (
            <Box
              data-testid="condition-branch-marker"
              sx={{
                position: 'absolute',
                width: 14,
                height: 14,
                backgroundColor: `${color}40`,
                border: `1px solid ${color}`,
                borderRadius: '2px',
                transform: 'rotate(45deg)',
              }}
            />
          )}
          {data.isIncomplete ? (
            <ErrorOutlineIcon sx={{ fontSize: 18, color, position: 'relative' }} />
          ) : hasRecommendation ? (
            <InfoOutlinedIcon
              sx={{ fontSize: 18, color, position: 'relative' }}
              data-testid="node-recommendation-icon"
            />
          ) : (
            <Icon sx={{ fontSize: 18, color, position: 'relative' }} />
          )}
        </Box>
        <Typography variant="caption" sx={{ fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {data.nodeType}
        </Typography>
      </Box>
      <Box sx={{ px: 1.5, py: 1 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            wordBreak: 'break-word',
            textDecoration: data.pendingDelete ? 'line-through' : 'none',
          }}
        >
          {data.label}
        </Typography>
        {/* TDD §3.1/§6 — persisted description (generated or user-authored), rendered as a
            subtitle exactly as the server returns it. */}
        {data.description && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.25, fontStyle: 'italic', wordBreak: 'break-word' }}
          >
            {data.description}
            {data.descriptionSource === 'user' && (
              <Typography
                component="span"
                variant="caption"
                data-testid="custom-description-indicator"
                sx={{ ml: 0.5, fontStyle: 'normal', fontWeight: 700, color: 'text.disabled' }}
              >
                (custom)
              </Typography>
            )}
          </Typography>
        )}
        {data.recipients && data.recipients.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {/* Matches RecipientPicker.tsx's own cosmetic suppression: an unresolved recipient
                (no email) is superseded the moment a real one exists alongside it, so this line
                shouldn't keep listing "Unassigned" once other, real recipients are present —
                purely display-only, data.recipients itself is untouched here. */}
            To: {(() => {
              const hasResolved = data.recipients.some((r) => r.email);
              const visible = hasResolved ? data.recipients.filter((r) => r.email) : data.recipients;
              return visible.map((r) => r.email || 'Unassigned').join(', ');
            })()}
          </Typography>
        )}
        {/* ADR §3 — hard-block indicator: distinct from the plain "Unassigned" label above, since
            this ACTION specifically requires a resolved approver, not just any recipient text. */}
        {data.isIncomplete && (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 700, color: '#d32f2f' }}>
            Recipient required
          </Typography>
        )}
        {/* UI density fix — the recommendation TEXT itself moved to the inspector panel
            (RuleFlowCanvas.tsx's selectedNode detail pane); the card keeps only the blue info
            icon above as a glance-able cue. Wrapping multi-line captions directly on every card
            was the actual clutter complaint on a flow of any real size — click the node to read
            what it says, same click-for-detail pattern the Label/Description fields already use. */}
      </Box>
      <Handle type="source" position={isHorizontal ? Position.Right : Position.Bottom} />
    </Box>
  );
}

export default memo(RuleNode);
